import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["searchInput", "faqItem", "tabButton", "tabContent"]

    connect() {
        console.log("FAQ controller connected")
    }

    // Search functionality
    filterItems(event) {
        const searchTerm = event.target.value.toLowerCase().trim()

        // Track which tabs have visible items
        const tabsWithVisibleItems = new Set()

        this.faqItemTargets.forEach(item => {
            const question = item.dataset.question || ""
            const answer = item.textContent.toLowerCase()
            const tabIndex = item.closest('[data-faq-target="tabContent"]')?.dataset.tabIndex

            if (question.includes(searchTerm) || answer.includes(searchTerm)) {
                item.classList.remove("hidden")
                if (tabIndex) {
                    tabsWithVisibleItems.add(tabIndex)
                }
            } else {
                item.classList.add("hidden")
            }
        })

        // Hide/show tabs based on whether they have visible items
        if (this.hasTabButtonTarget) {
            this.tabButtonTargets.forEach(button => {
                const tabIndex = button.dataset.tabIndex
                if (searchTerm && !tabsWithVisibleItems.has(tabIndex)) {
                    button.classList.add("hidden")
                } else {
                    button.classList.remove("hidden")
                }
            })

            // Hide/show tab contents based on search
            this.tabContentTargets.forEach(content => {
                const tabIndex = content.dataset.tabIndex
                if (searchTerm && !tabsWithVisibleItems.has(tabIndex)) {
                    content.classList.add("hidden")
                }
            })

            // If current active tab is hidden, show first visible tab
            if (searchTerm) {
                const activeButton = this.tabButtonTargets.find(btn =>
                    btn.classList.contains("bg-primary") && !btn.classList.contains("hidden")
                )

                if (!activeButton) {
                    const firstVisibleButton = this.tabButtonTargets.find(btn => !btn.classList.contains("hidden"))
                    if (firstVisibleButton) {
                        firstVisibleButton.click()
                    }
                }
            }
        }

        // Show "No results" message if all items are hidden
        this.updateNoResultsMessage(searchTerm)
    }

    updateNoResultsMessage(searchTerm) {
        const visibleItems = this.faqItemTargets.filter(item => !item.classList.contains("hidden"))

        // Remove existing no results message
        const existingMessage = this.element.querySelector(".faq-no-results")
        if (existingMessage) {
            existingMessage.remove()
        }

        // Add no results message if needed
        if (visibleItems.length === 0 && searchTerm) {
            const container = this.element.querySelector(".bg-grey-custom, .grid")
            if (container) {
                const noResultsDiv = document.createElement("div")
                noResultsDiv.className = "faq-no-results text-center py-10 text-gray-500"
                noResultsDiv.innerHTML = `
          <svg class="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <p class="text-lg font-medium">No FAQs found</p>
          <p class="text-sm mt-2">Try adjusting your search terms</p>
        `
                container.appendChild(noResultsDiv)
            }
        }
    }

    // Tab switching functionality
    switchTab(event) {
        const clickedButton = event.currentTarget
        const tabIndex = clickedButton.dataset.tabIndex

        // Update button states
        this.tabButtonTargets.forEach(button => {
            if (button.dataset.tabIndex === tabIndex) {
                button.classList.remove("bg-white", "text-gray-800", "hover:bg-gray-100")
                button.classList.add("bg-btn-dark", "text-white")
            } else {
                button.classList.remove("bg-btn-dark", "text-white")
                button.classList.add("bg-white", "text-gray-800", "hover:bg-gray-100")
            }
        })

        // Update tab content visibility
        this.tabContentTargets.forEach(content => {
            if (content.dataset.tabIndex === tabIndex) {
                content.classList.remove("hidden")
                // Add fade-in animation
                content.style.opacity = "0"
                setTimeout(() => {
                    content.style.transition = "opacity 0.3s ease-in-out"
                    content.style.opacity = "1"
                }, 10)
            } else {
                content.classList.add("hidden")
            }
        })
    }

    // Clear search
    clearSearch() {
        if (this.hasSearchInputTarget) {
            this.searchInputTarget.value = ""
            this.faqItemTargets.forEach(item => {
                item.classList.remove("hidden")
            })

            // Show all tabs again
            if (this.hasTabButtonTarget) {
                this.tabButtonTargets.forEach(button => {
                    button.classList.remove("hidden")
                })
                this.tabContentTargets.forEach(content => {
                    // Only show the active tab content
                    const isActive = this.tabButtonTargets.find(btn =>
                        btn.dataset.tabIndex === content.dataset.tabIndex &&
                        btn.classList.contains("bg-primary")
                    )
                    if (!isActive) {
                        content.classList.add("hidden")
                    }
                })
            }

            // Remove no results message
            const existingMessage = this.element.querySelector(".faq-no-results")
            if (existingMessage) {
                existingMessage.remove()
            }
        }
    }
}
